import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SET_USER } from "redux/types/user";
import { TextField, Typography, Button, Checkbox, FormError } from "components";
import { Formik, Form, ErrorMessage } from "formik";
import { ROUTES, pocketMoniUrl } from "routes/routePaths";
import { routes } from "routes/apiRoutes";
import { setAlert } from "utils/helper";
import axios from "axios";
import qs from "qs";
import {
  encryptData,
  getDecodedToken,
  isAuthenticated,
  isAdminUser,
  isUserActive,
} from "utils/helper";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";
import loginImage from "assets/images/login-gif.gif";
import pmLogo from "assets/images/pocketmoni-logo.png";
import * as Yup from "yup";
import { FaRegUser } from "react-icons/fa";
import { HiOutlineLockClosed } from "react-icons/hi";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import "./style.scss";

const Login = () => {
  const [passwordType, setPasswordType] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);

  const handlePasswordType = () => setPasswordType(!passwordType);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const getRouteToNavigate = (user) => {
    let routeToNavigate;

    const { dashboard, dashboardTerminalStats, requestNin } = ROUTES;

    if (isAdminUser(user)) {
      routeToNavigate = dashboardTerminalStats;
    } else if (!user.nin) {
      routeToNavigate = requestNin;
    } else {
      routeToNavigate = isUserActive(user) ? dashboardTerminalStats : dashboard;
    }

    return routeToNavigate;
  };

  const onSubmit = async ({ username, password }) => {
    setError(null);
    // if (rememberMe)
    // Cookies.set("username", encryptData(username), { sameSite: "strict" });

    try {
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + routes.getToken(),
        qs.stringify({
          username: username.trim(),
          password: password.trim(),
          grant_type: process.env.REACT_APP_GRANT_TYPE,
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: "Basic VE1TX1BPUlRBTF9DTElFTlQ6cGFzc3dvcmQxMjM0",
          },
        }
      );

      if (data.responseCode === 419) {
        navigate(ROUTES.passwordChange, {
          state: { passKey: data.passkey, username },
        });
      } else {
        const decodedToken = getDecodedToken(data.access_token);

        dispatch({
          type: SET_USER,
          payload: decodedToken,
        });

        localStorage.setItem("access_token", encryptData(data.access_token));
        localStorage.setItem("current_user", JSON.stringify(decodedToken));

        navigate(getRouteToNavigate(decodedToken));
      }
    } catch (err) {
      const { status, data } = err.response || {};
      if (status.toString().startsWith("4")) setError(data.error_description);

      if (status === 500) {
        setAlert("Something went wrong, please try again");
      }
    }
  };

  //Make sure user is not trying to log in again if he/she still has a current sesssion
  if (isAuthenticated()) {
    return <Navigate to={ROUTES.dashboard} />;
  }

  return (
    <div className="row m-0 auth-container">
      <div className="auth-header-container">
        <AuthHeader />
      </div>
      <div className="col-12 col-xl-6 p-0 auth-form-container">
        <div className="login-form">
          <div className="auth-intro">
            <div className="mb-4 d-xl-none">
              <a href={pocketMoniUrl} target="_blank" rel="noopener noreferrer">
                <img src={pmLogo} width="150" alt="PocketMoni" />
              </a>{" "}
            </div>
            <Typography component="h3" variant="heading3" state="primary">
              Sign In to your account
            </Typography>

            <Typography variant="body3" state="primary">
              Enter the details below to sign in to your account
            </Typography>
          </div>

          <Formik
            initialValues={{
              username: "",
              password: "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values) => await onSubmit(values)}
          >
            {({ errors, touched, isSubmitting, onFocus, getFieldProps }) => (
              <Form noValidate autoComplete="off">
                {error && (
                  <Typography variant="body2" state="danger">
                    {error}
                  </Typography>
                )}

                <div className="mb-4">
                  <TextField
                    name="username"
                    type="text"
                    placeholder="Enter username"
                    label="Username"
                    {...getFieldProps("username")}
                    onFocus={() => setError(null)}
                    hasError={errors.username && touched.username}
                    startIcon={<FaRegUser color="#AAA6A6" />}
                  />
                  <ErrorMessage component={FormError} name="username" />
                </div>
                <TextField
                  name="password"
                  type={passwordType ? "password" : "text"}
                  placeholder="Enter Password"
                  label="Password"
                  onFocus={() => setError(null)}
                  {...getFieldProps("password")}
                  hasError={errors.password && touched.password}
                  startIcon={
                    <HiOutlineLockClosed color="#AAA6A6" fontSize="19px" />
                  }
                  endIcon={
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handlePasswordType}
                    >
                      {passwordType ? (
                        <AiOutlineEye />
                      ) : (
                        <AiOutlineEyeInvisible />
                      )}
                    </div>
                  }
                />
                <ErrorMessage component={FormError} name="password" />
                <div
                  style={{ marginBottom: "3rem" }}
                  className="d-flex justify-content-between mt-1"
                >
                  <section className="d-flex align-items-center">
                    <Checkbox
                      name="rememberMe"
                      onChange={(e) => setRememberMe(e.target.checked)}
                      checked={rememberMe}
                    />{" "}
                    &nbsp;
                    <Typography variant="body3" state="primary">
                      Remember Me
                    </Typography>
                  </section>
                  <Link to={ROUTES.passwordForgot}>
                    <Typography variant="body3" state="primary">
                      Forgot Password?
                    </Typography>
                  </Link>
                </div>
                <Button
                  type="submit"
                  label="Sign in"
                  loading={isSubmitting}
                  fullWidth
                />
              </Form>
            )}
          </Formik>
          <div className="text-center mt-2 mb-4">
            <Typography component="span" variant="body3">
              Not Registered Yet?
            </Typography>{" "}
            <Link to={ROUTES.register}>
              <Typography variant="body2" state="primary" component="span">
                Become an Agent
              </Typography>
            </Link>
          </div>
        </div>
        <AuthFooter showLicense />
      </div>

      <div className="d-none d-xl-block col-xl-6 p-0">
        <img src={loginImage} width="100%" alt="Log In" />
      </div>
    </div>
  );
};

export default Login;
